
import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar, Clock, MapPin, ChevronRight, Search } from 'lucide-react';
import TranslatedText from '@/TranslatedText';
import { getEvents, Event } from '@/data/eventsData';

const NewsEvents: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  
  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="mb-10 text-center">
          <h1 className="text-3xl font-bold mb-3">
            <TranslatedText text="News & Events" />
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            <TranslatedText text="Stay updated with the latest news, announcements, and upcoming events from Ahavex and our partner communities." />
          </p>
        </div>

        {/* Search Bar */}
        <div className="relative mb-8 max-w-md mx-auto">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input 
            type="text" 
            placeholder="Search news and events..." 
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* News & Events Tabs */}
        <Tabs defaultValue="upcoming" className="mb-16">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-3">
            <TabsTrigger value="upcoming"><TranslatedText text="Upcoming Events" /></TabsTrigger>
            <TabsTrigger value="news"><TranslatedText text="Latest News" /></TabsTrigger>
            <TabsTrigger value="past"><TranslatedText text="Past Events" /></TabsTrigger>
          </TabsList>
          
          {/* Upcoming Events Tab */}
          <TabsContent value="upcoming" className="mt-6">
            <UpcomingEventsSection />
          </TabsContent>
          
          {/* Latest News Tab */}
          <TabsContent value="news" className="mt-6">
            <LatestNewsSection />
          </TabsContent>
          
          {/* Past Events Tab */}
          <TabsContent value="past" className="mt-6">
            <PastEventsSection />
          </TabsContent>
        </Tabs>
        
        {/* Newsletter Signup */}
        <Card className="mb-16">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-xl font-bold mb-2">
                  <TranslatedText text="Stay Informed" />
                </h3>
                <p className="text-muted-foreground">
                  <TranslatedText text="Subscribe to our newsletter to get the latest updates delivered to your inbox." />
                </p>
              </div>
              <div className="flex w-full md:w-auto gap-2">
                <Input 
                  placeholder="Your email address" 
                  type="email" 
                  className="max-w-xs"
                />
                <Button>
                  <TranslatedText text="Subscribe" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

const UpcomingEventsSection: React.FC = () => {
  const upcomingEvents = getEvents();

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold mb-6">
        <TranslatedText text="Upcoming Events" />
      </h2>
      
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {upcomingEvents.map(event => (
          <Card key={event.id} className="overflow-hidden">
            <div className="aspect-video bg-muted">
              <img src={event.image} alt={event.name} className="object-cover w-full h-full" />
            </div>
            <CardHeader>
              <CardTitle>{event.name}</CardTitle>
              <CardDescription>
                <div className="flex items-center mt-2">
                  <Calendar className="h-4 w-4 mr-2" />
                  <span>{event.date}</span>
                </div>
                <div className="flex items-center mt-1">
                  <Clock className="h-4 w-4 mr-2" />
                  <span>{event.time}</span>
                </div>
                <div className="flex items-center mt-1">
                  <MapPin className="h-4 w-4 mr-2" />
                  <span>{event.location}</span>
                </div>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm">{event.description}</p>
            </CardContent>
            <CardFooter>
              <Button variant="outline" className="w-full">
                <TranslatedText text="Register" />
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>
      
      <div className="mt-8 text-center">
        <Button variant="outline">
          <TranslatedText text="View All Upcoming Events" />
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

const LatestNewsSection: React.FC = () => {
  const latestNews = [
    {
      id: 1,
      title: "Ahavex Expands to Three New African Countries",
      date: "August 28, 2023",
      summary: "Ahavex announces expansion into Ghana, Kenya, and Ethiopia, bringing its platform to even more African entrepreneurs.",
      category: "Company News",
      image: "/placeholder.svg"
    },
    {
      id: 2,
      title: "New Payment Options Available for Customers",
      date: "August 15, 2023",
      summary: "We've added new payment methods including mobile money options to make shopping even more convenient.",
      category: "Product Updates",
      image: "/placeholder.svg"
    },
    {
      id: 3,
      title: "Ahavex Foundation Launches Education Initiative",
      date: "July 30, 2023",
      summary: "Our foundation is launching a new program to provide business education to rural entrepreneurs across Rwanda.",
      category: "Community",
      image: "/placeholder.svg"
    },
    {
      id: 4,
      title: "Quarterly Business Report: Q2 2023",
      date: "July 15, 2023",
      summary: "See how Ahavex has grown in the second quarter and our plans for continued expansion and improvement.",
      category: "Reports",
      image: "/placeholder.svg"
    }
  ];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold mb-6">
        <TranslatedText text="Latest News" />
      </h2>
      
      <div className="space-y-6">
        {latestNews.map(news => (
          <Card key={news.id}>
            <div className="md:flex">
              <div className="md:w-1/4 aspect-video md:aspect-square bg-muted">
                <img src={news.image} alt={news.title} className="object-cover w-full h-full" />
              </div>
              <div className="md:w-3/4">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle>{news.title}</CardTitle>
                      <CardDescription className="mt-2">
                        <span className="inline-block bg-muted px-2 py-1 rounded text-xs mr-2">
                          {news.category}
                        </span>
                        <span>{news.date}</span>
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p>{news.summary}</p>
                </CardContent>
                <CardFooter>
                  <Button variant="link" className="p-0">
                    <TranslatedText text="Read More" />
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </CardFooter>
              </div>
            </div>
          </Card>
        ))}
      </div>
      
      <div className="mt-8 text-center">
        <Button variant="outline">
          <TranslatedText text="View All News" />
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

const PastEventsSection: React.FC = () => {
  const pastEvents = [
    {
      id: 1,
      title: "Entrepreneur Showcase 2023",
      date: "June 20, 2023",
      location: "Kigali Innovation Village",
      description: "A platform for African entrepreneurs to showcase their products and services to potential investors and partners.",
      image: "/placeholder.svg"
    },
    {
      id: 2,
      title: "Digital Marketing Masterclass",
      date: "May 15, 2023",
      location: "Virtual Event",
      description: "Expert-led sessions on leveraging digital marketing to grow African businesses.",
      image: "/placeholder.svg"
    },
    {
      id: 3,
      title: "Supply Chain Management Workshop",
      date: "April 10, 2023",
      location: "Ahavex Training Center, Kigali",
      description: "Practical workshop on optimizing supply chains for small and medium enterprises.",
      image: "/placeholder.svg"
    }
  ];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold mb-6">
        <TranslatedText text="Past Events" />
      </h2>
      
      <div className="space-y-4">
        {pastEvents.map(event => (
          <Card key={event.id}>
            <div className="md:flex">
              <div className="md:w-1/4 aspect-video bg-muted">
                <img src={event.image} alt={event.title} className="object-cover w-full h-full" />
              </div>
              <div className="md:w-3/4">
                <CardHeader>
                  <CardTitle>{event.title}</CardTitle>
                  <CardDescription>
                    <div className="flex items-center mt-1">
                      <Calendar className="h-4 w-4 mr-2" />
                      <span>{event.date}</span>
                    </div>
                    <div className="flex items-center mt-1">
                      <MapPin className="h-4 w-4 mr-2" />
                      <span>{event.location}</span>
                    </div>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">{event.description}</p>
                </CardContent>
                <CardFooter>
                  <Button variant="outline" size="sm">
                    <TranslatedText text="View Recap" />
                  </Button>
                </CardFooter>
              </div>
            </div>
          </Card>
        ))}
      </div>
      
      <div className="mt-8 text-center">
        <Button variant="outline">
          <TranslatedText text="View All Past Events" />
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default NewsEvents;
